import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { 
  Shield, Zap, Compass, Fuel, Headphones, X, 
  MessageCircle, Weight, Ruler, Box
} from 'lucide-react';
import { WHATSAPP_LINK } from '../utils/constants';

// --- DATA ---

const categories = [
  { id: 'all', label: 'All' },
  { id: 'mini', label: 'Mini Cargo' },
  { id: 'medium', label: 'Medium Cargo' },
  { id: 'furniture', label: 'Furniture' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'heavy', label: 'Heavy Logistics' },
];

const transportOptions = [
  { id: 'parcels', label: 'Parcels', suggest: 1 },
  { id: 'appliances', label: 'Appliances', suggest: 2 },
  { id: 'furniture', label: 'Furniture', suggest: 3 },
  { id: 'industrial', label: 'Industrial Goods', suggest: 4 },
  { id: 'construction', label: 'Construction Materials', suggest: 5 },
  { id: 'bulk', label: 'Bulk Cargo', suggest: 6 },
];

const trucks = [
  {
    id: 1,
    name: 'Dost Plus',
    category: 'mini',
    subCategory: 'Mini Cargo Vehicle',
    capacity: 'Up to 1 Ton',
    img: '/images/dostplus.png',
    size: '10 FT',
    description: 'Perfect for urban deliveries and local city logistics with quick turnaround.',
    useCases: ['Small Deliveries', 'Parcel Service', 'Local City Logistics', 'Quick Transport'],
    specs: { payload: '1000 kg', dimensions: '10 x 6 x 6 ft', engine: '75 HP', torque: '180 Nm' },
    color: '#FF6B00',
    visualFeel: 'Urban roads, Fast delivery vive',
  },
  {
    id: 2,
    name: 'Bada Dost i5',
    category: 'medium',
    subCategory: 'Medium Cargo Vehicle',
    capacity: 'Up to 2 Ton',
    img: '/images/Badadost.png',
    size: '12 FT',
    description: 'The industrial powerhouse for medium commercial cargo and warehouse logistics.',
    useCases: ['Appliances Transport', 'Electronics Delivery', 'Retail Goods', 'Medium Commercial Cargo'],
    specs: { payload: '2000 kg', dimensions: '12 x 6.5 x 6.5 ft', engine: '80 HP', torque: '200 Nm' },
    color: '#FF8C00',
    visualFeel: 'Warehouse background, Loading dock visuals',
  },
  {
    id: 3,
    name: 'Eicher 2059XP',
    category: 'furniture',
    subCategory: 'Furniture & Household Logistics',
    capacity: 'Up to 3 Ton',
    img: '/images/Eicher2059.png',
    size: '14 FT',
    description: 'Specialized for household shifting and professional furniture logistics.',
    useCases: ['Furniture Transport', 'Household Shifting', 'Office Relocation', 'Home Moving'],
    specs: { payload: '3000 kg', dimensions: '14 x 7 x 7 ft', engine: '100 HP', torque: '250 Nm' },
    color: '#E65C00',
    visualFeel: 'Apartment relocation, Furniture logistics',
  },
  // Hero Heavy Fleet
  { id: 4, name: '17 Feet Truck', category: 'heavy', subCategory: 'Commercial Heavy Logistics', capacity: 'Up to 5 Ton+', img: '/images/17feet.png', size: '17 FT', description: 'Advanced heavy-duty carrier for industrial materials and large commercial shipments.', tags: ['Industrial', 'Interstate'], specs: { payload: '5000 kg', dimensions: '17 x 8 x 8 ft' } },
  { id: 5, name: '19 Feet Truck', category: 'heavy', subCategory: 'Commercial Heavy Logistics', capacity: 'Up to 7 Ton+', img: '/images/19feet.png', size: '19 FT', description: 'Maximum reliability for long-haul interstate logistics and bulk industrial transport.', tags: ['Industrial', 'Commercial'], specs: { payload: '7000 kg', dimensions: '19 x 8 x 8 ft' } },
  { id: 6, name: '20 Feet Truck', category: 'heavy', subCategory: 'Commercial Heavy Logistics', capacity: 'Up to 8 Ton+', img: '/images/20feet.png', size: '20 FT', description: 'High-capacity cargo vehicle designed for bulk goods and secondary distribution.', tags: ['Bulk Cargo', 'Interstate'], specs: { payload: '8000 kg', dimensions: '20 x 8 x 8 ft' } },
  { id: 7, name: '22 Feet Truck', category: 'heavy', subCategory: 'Commercial Heavy Logistics', capacity: 'Up to 10 Ton+', img: '/images/22feet.png', size: '22 FT', description: 'Professional heavy logistics solution for construction materials and wholesale goods.', tags: ['Construction', 'Interstate'], specs: { payload: '10000 kg', dimensions: '22 x 8 x 8 ft' } },
  { id: 8, name: '24 Feet Truck', category: 'heavy', subCategory: 'Commercial Heavy Logistics', capacity: 'Up to 12 Ton+', img: '/images/24feet.png', size: '24 FT', description: 'Extra-large fleet vehicle for major industrial bulk logistics and heavy infrastructure.', tags: ['Industrial Bulk', 'Wholesale'], specs: { payload: '12000 kg', dimensions: '24 x 8 x 8 ft' } },
  { id: 9, name: '32 Feet Truck', category: 'heavy', subCategory: 'Commercial Heavy Logistics', capacity: 'Up to 15 Ton+', img: '/images/32feet.png', size: '32 FT', description: 'Our highest capacity interstate logistics powerhouse for pan-India operations.', tags: ['Interstate', 'Wholesale'], specs: { payload: '15000 kg', dimensions: '32 x 8 x 8 ft' } },
];

const features = [
  { icon: Compass, label: 'GPS Enabled' },
  { icon: Fuel, label: 'Fuel Efficient' },
  { icon: Shield, label: 'Safe Transport' },
  { icon: Headphones, label: '24/7 Support' },
  { icon: Zap, label: 'Industrial Performance' },
];

// --- COMPONENTS ---

const GlowingDivider = () => (
  <div className="relative w-full max-w-lg mx-auto h-4 flex items-center justify-center my-8">
    <motion.div 
      initial={{ width: 0, opacity: 0 }}
      whileInView={{ width: '100%', opacity: 1 }}
      transition={{ duration: 1.5, ease: "circOut" }}
      className="absolute h-[1px] bg-gradient-to-r from-transparent via-brand-orange to-transparent"
    />
    <motion.div 
      animate={{ 
        boxShadow: ["0 0 5px #FF6B00", "0 0 20px #FF6B00", "0 0 5px #FF6B00"],
        scale: [1, 1.2, 1]
      }}
      transition={{ duration: 2, repeat: Infinity }}
      className="w-2 h-2 rounded-full bg-brand-orange z-10"
    />
  </div>
);

const FleetCard = ({ truck, onSelect, isHeavy = false }) => {
  const cardRef = useRef(null);

  useEffect(() => {
    if (cardRef.current) {
      gsap.to(cardRef.current, {
        y: -10,
        repeat: -1,
        yoyo: true,
        duration: 3 + Math.random() * 2,
        ease: "power1.inOut"
      });
    }
  }, []);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -5 }}
      onClick={() => onSelect(truck)}
      className="group relative rounded-3xl p-px overflow-hidden cursor-pointer aspect-[4/5.5] md:aspect-auto md:min-h-[480px]"
    >
      {/* Animated Border Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/20 via-transparent to-brand-orange/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      {/* Glass Content */}
      <div className="relative h-full w-full bg-white/70 backdrop-blur-xl border border-black/5 rounded-3xl p-4 md:p-6 flex flex-col justify-between overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-500">
        {/* Subtle Glow */}
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-brand-orange/5 blur-[80px] pointer-events-none group-hover:bg-brand-orange/10 transition-all duration-700" />

        {/* Top Info */}
        <div className="z-10">
          <div className="flex justify-between items-start mb-4">
            <span className="text-[10px] uppercase tracking-[0.2em] font-black text-brand-orange bg-brand-orange/10 px-3 py-1 rounded-full border border-brand-orange/20">
              {truck.size}
            </span>
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-600">
              {truck.capacity}
            </span>
          </div>
          <h3 className="text-xl md:text-2xl font-black text-slate-950 leading-tight uppercase group-hover:text-brand-orange transition-colors duration-300">
            {truck.name}
          </h3>
          <p className="text-slate-700 text-[11px] font-medium mt-1 uppercase tracking-widest">
            {truck.subCategory}
          </p>
        </div>

        {/* Image - Floating Effect */}
        <div ref={cardRef} className="relative py-4 flex items-center justify-center">
          <motion.img 
            src={truck.img} 
            alt={truck.name}
            className="w-full max-w-[280px] md:max-w-[240px] drop-shadow-[0_20px_40px_rgba(0,0,0,0.1)] group-hover:scale-110 transition-transform duration-700 ease-out"
          />
          {/* Floor Shadow */}
          <div className="absolute bottom-0 w-1/2 h-4 bg-black/5 blur-xl rounded-full scale-x-125" />
        </div>

        {/* Bottom Actions */}
        <div className="z-10 flex flex-col gap-3">
          <div className="flex flex-wrap gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
            {(truck.useCases || truck.tags).slice(0, 2).map((tag, i) => (
              <span key={i} className="text-[9px] text-slate-600 border border-black/5 px-2 py-0.5 rounded backdrop-blur-md bg-white/20">
                {tag}
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <button className="flex-1 bg-black/[0.03] hover:bg-black/[0.08] border border-black/5 py-3 rounded-xl text-[10px] font-black tracking-widest text-slate-900 uppercase transition-all">
              Details
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                window.open(WHATSAPP_LINK(`Hi PKRouteX, I want to book ${truck.name}.`), '_blank');
              }}
              className="flex-1 bg-brand-orange hover:bg-brand-orange-light py-3 rounded-xl text-[10px] font-black tracking-widest text-white uppercase transition-all shadow-lg shadow-brand-orange/20"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>

      {/* Hover Light Streak */}
      <div className="absolute top-0 -left-full w-2/3 h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-25deg] group-hover:left-[150%] transition-all duration-1000 ease-in-out" />
    </motion.div>
  );
};

const SmartSelector = ({ onSuggest }) => {
  const [selected, setSelected] = useState(null);

  const handleSelect = (option) => {
    setSelected(option.id);
    onSuggest(option.suggest);
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white/50 backdrop-blur-3xl border border-black/5 rounded-[2rem] p-5 md:p-12 mb-12 md:mb-20 shadow-2xl shadow-black/5">
      <div className="text-center mb-6 md:mb-8">
        <h4 className="text-brand-orange font-black tracking-[0.3em] text-xs uppercase mb-2">Smart Sizing</h4>
        <h3 className="text-xl md:text-4xl font-black text-slate-900 uppercase tracking-tight">What Are You <span className="text-brand-orange">Transporting?</span></h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {transportOptions.map((opt) => (
          <button
            key={opt.id}
            onClick={() => handleSelect(opt)}
            className={`group relative py-4 md:py-6 px-3 md:px-4 rounded-2xl border transition-all duration-500 flex flex-col items-center gap-2 md:gap-3 ${
              selected === opt.id 
                ? 'bg-brand-orange border-brand-orange shadow-lg shadow-brand-orange/20' 
                : 'bg-slate-50 border-black/5 hover:border-brand-orange/50 hover:bg-white'
            }`}
          >
            <div className={`p-3 rounded-xl transition-colors ${selected === opt.id ? 'bg-white/20' : 'bg-brand-orange/5 text-brand-orange group-hover:bg-brand-orange group-hover:text-white'}`}>
              <Box size={24} className={selected === opt.id ? 'text-white' : ''}/>
            </div>
            <span className={`text-xs font-black uppercase tracking-widest ${selected === opt.id ? 'text-white' : 'text-slate-800'}`}>
              {opt.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const FleetModal = ({ truck, onClose }) => {
  if (!truck) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 md:p-10"
    >
      {/* Backdrop */}
      <motion.div 
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-white/80 backdrop-blur-2xl"
      />

      {/* Modal Card */}
      <motion.div 
        initial={{ y: 100, opacity: 0, scale: 0.9 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 100, opacity: 0, scale: 0.9 }}
        className="relative w-full max-w-6xl bg-white border border-black/5 rounded-[2rem] md:rounded-[3rem] overflow-hidden flex flex-col md:flex-row max-h-[92vh] md:max-h-[90vh] overflow-y-auto shadow-2xl shadow-black/10"
      >
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 z-20 w-12 h-12 rounded-full bg-slate-50 border border-black/5 flex items-center justify-center text-slate-900 hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-all group"
        >
          <X size={20} className="group-hover:rotate-90 transition-transform"/>
        </button>

        {/* Left: Visuals */}
        <div className="md:w-1/2 relative bg-slate-50 p-10 flex flex-col items-center justify-center min-h-[350px]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-orange/5 via-transparent to-transparent opacity-50" />
          <motion.img 
            initial={{ scale: 0.8, rotate: -5 }}
            animate={{ scale: 1, rotate: 0 }}
            src={truck.img} 
            className="w-full max-w-[450px] relative z-10 drop-shadow-[0_20px_40px_rgba(0,0,0,0.1)]"
          />
          <div className="mt-10 text-center relative z-10">
            <span className="text-[10px] font-black tracking-[0.4em] text-brand-orange uppercase">{truck.category}</span>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mt-2 uppercase tracking-tighter">{truck.name}</h2>
            <div className="h-1 w-20 bg-brand-orange mx-auto mt-4 rounded-full" />
          </div>
        </div>

        {/* Right: Details */}
        <div className="md:w-1/2 p-8 md:p-14 bg-white flex flex-col md:overflow-y-auto">
          <div className="space-y-10">
            {/* Quick Specs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-3 md:p-4 rounded-2xl border border-black/5">
                <Weight className="text-brand-orange mb-2" size={18}/>
                <p className="text-[9px] md:text-[10px] text-slate-600 uppercase font-bold mb-1">Payload</p>
                <p className="text-slate-950 font-black text-xs md:text-sm">{truck.specs.payload}</p>
              </div>
              <div className="bg-slate-50 p-3 md:p-4 rounded-2xl border border-black/5">
                <Ruler className="text-brand-orange mb-2" size={18}/>
                <p className="text-[9px] md:text-[10px] text-slate-600 uppercase font-bold mb-1">Dimensions</p>
                <p className="text-slate-950 font-black text-xs md:text-sm">{truck.specs.dimensions}</p>
              </div>
              <div className="bg-slate-50 p-3 md:p-4 rounded-2xl border border-black/5">
                <Zap className="text-brand-orange mb-2" size={18}/>
                <p className="text-[9px] md:text-[10px] text-slate-600 uppercase font-bold mb-1">Power</p>
                <p className="text-slate-950 font-black text-xs md:text-sm">{truck.specs.engine || 'High-Spec'}</p>
              </div>
              <div className="bg-slate-50 p-3 md:p-4 rounded-2xl border border-black/5">
                <Fuel className="text-brand-orange mb-2" size={18}/>
                <p className="text-[9px] md:text-[10px] text-slate-600 uppercase font-bold mb-1">Efficiency</p>
                <p className="text-slate-950 font-black text-xs md:text-sm">Industrial</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="text-slate-700 text-lg leading-relaxed font-light italic mb-8 border-l-2 border-brand-orange pl-6">
                "{truck.description}"
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-slate-900 font-black uppercase text-xs tracking-widest mb-4 flex items-center gap-2">
                    <Box size={14} className="text-brand-orange"/> Use Cases
                  </h4>
                  <ul className="space-y-2">
                    {(truck.useCases || truck.tags).map((use, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-orange" /> {use}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-slate-900 font-black uppercase text-xs tracking-widest mb-4 flex items-center gap-2">
                    <Shield size={14} className="text-brand-orange"/> Technology
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-black/5 text-[10px] text-slate-950 font-bold">
                        <f.icon size={12} className="text-brand-orange"/> {f.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="pt-10 flex flex-col sm:flex-row gap-4 mt-auto">
              <button 
                onClick={() => {
                  window.open(WHATSAPP_LINK(`Hi PKRouteX! I want to book the *${truck.name}* for my logistics needs.`), '_blank');
                }}
                className="flex-1 py-5 rounded-2xl bg-brand-orange hover:bg-brand-orange-light text-white font-black tracking-[0.2em] uppercase transition-all shadow-xl shadow-brand-orange/20"
              >
                Book Now
              </button>
              <button 
                onClick={() => {
                  window.open(WHATSAPP_LINK(`Inquiry about ${truck.name} specifications and routes.`), '_blank');
                }}
                className="flex-1 py-5 rounded-2xl border border-black/5 bg-slate-50 hover:bg-slate-100 text-slate-900 font-black tracking-[0.2em] uppercase transition-all flex items-center justify-center gap-3"
              >
                <MessageCircle size={20} className="text-green-500" /> WhatsApp
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// --- MAIN SECTION ---

const Fleet = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedTruck, setSelectedTruck] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!sectionRef.current) return;
      const glow = document.querySelector('.fleet-glow');
      if (glow) {
        gsap.to(glow, {
          x: e.clientX - 200,
          y: e.clientY - 200,
          opacity: 1,
          duration: 0.5,
          ease: "power2.out"
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const filteredTrucks = useMemo(() => {
    if (activeFilter === 'all') return trucks;
    if (activeFilter === 'heavy') return trucks.filter(t => t.category === 'heavy');
    if (activeFilter === 'commercial') return trucks.filter(t => t.category === 'medium' || t.category === 'heavy');
    return trucks.filter(t => t.category === activeFilter);
  }, [activeFilter]);

  const scrollToTruck = (truckId) => {
    const truck = trucks.find(t => t.id === truckId);
    if (truck) {
      setSelectedTruck(truck);
    }
  };

  return (
    <section 
      id="fleet" 
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-slate-50 overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-brand-orange/[0.03] rounded-full blur-[150px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-brand-orange/[0.03] rounded-full blur-[150px] translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Intro */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col items-center"
          >
            <span className="text-brand-orange font-black tracking-[0.6em] text-xs uppercase mb-4 py-1 px-4 border border-brand-orange/20 rounded-full bg-brand-orange/5">
              Premium Logistics
            </span>
            <h2 className="text-4xl sm:text-6xl md:text-8xl font-black text-slate-950 tracking-tighter uppercase leading-none mb-6">
              OUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-orange to-brand-orange-light">FLEET</span>
            </h2>
            <p className="text-brand-orange text-xl md:text-2xl font-black uppercase tracking-widest mb-4">
              Vehicles Built For Every Delivery Need
            </p>
            <p className="text-slate-700 text-sm md:text-base max-w-2xl mx-auto font-medium leading-relaxed">
              From small parcel transport to large interstate shipments, PKROUTEX provides the perfect logistics vehicle for every business requirement.
            </p>
          </motion.div>
          
          <GlowingDivider />
        </div>

        {/* Smart UX Feature */}
        <SmartSelector onSuggest={scrollToTruck} />

        {/* Filter System */}
        <div className="flex flex-wrap justify-center gap-2 mb-10 md:mb-20">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveFilter(cat.id)}
              className={`px-6 md:px-10 py-3 md:py-4 rounded-full text-[11px] font-black uppercase tracking-[0.2em] transition-all duration-300 border ${
                activeFilter === cat.id 
                  ? 'bg-brand-orange border-brand-orange text-white shadow-xl shadow-brand-orange/30' 
                  : 'bg-white border-black/5 text-slate-600 hover:border-brand-orange/50 hover:text-slate-950 shadow-sm'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Main Grid / Carousel */}
        <AnimatePresence mode="popLayout">
          <motion.div 
            className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8 overflow-x-auto md:overflow-visible pb-12 md:pb-0 snap-x snap-proximity scrollbar-hide px-4 md:px-0"
            layout
          >
            {filteredTrucks.map((truck) => (
              <div key={truck.id} className="min-w-[280px] flex-shrink-0 md:min-w-0 snap-start">
                <FleetCard 
                  truck={truck} 
                  onSelect={setSelectedTruck}
                  isHeavy={truck.category === 'heavy'}
                />
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Heavy Logistics Hero Banner (Only when 'all' or 'heavy' is selected) */}
        {(activeFilter === 'all' || activeFilter === 'heavy') && (
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 md:mt-32 relative rounded-[2rem] md:rounded-[3rem] overflow-hidden bg-white border border-brand-orange/10 p-6 md:p-16 text-center md:text-left flex flex-col md:flex-row items-center gap-8 md:gap-10 shadow-2xl shadow-black/[0.03]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-slate-50 via-transparent to-transparent z-0 hidden md:block" />
            <div className="md:w-1/2 relative z-10">
              <h3 className="text-2xl md:text-6xl font-black text-slate-950 uppercase tracking-tighter leading-none mb-4 md:mb-6">
                Commercial <span className="text-brand-orange">Heavy</span> <br />Logistics Fleet
              </h3>
              <p className="text-slate-700 text-lg mb-8 font-medium">
                Built for industrial transport, wholesale cargo movement, bulk logistics, and interstate commercial operations.
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <div className="bg-brand-orange/5 border border-brand-orange/10 px-6 py-3 rounded-2xl">
                  <p className="text-brand-orange font-black text-2xl">17 – 32 FT</p>
                  <p className="text-[10px] text-slate-800 uppercase font-black tracking-widest">Size Options</p>
                </div>
                <div className="bg-slate-50 border border-slate-100 px-6 py-3 rounded-2xl">
                  <p className="text-slate-950 font-black text-2xl">Up to 15 Ton</p>
                  <p className="text-[10px] text-slate-600 uppercase font-black tracking-widest">Max Capacity</p>
                </div>
              </div>
            </div>
            <div className="md:w-1/2 relative">
               <motion.img 
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  src="/images/Eicherallfeet.png" 
                  className="w-full drop-shadow-[0_20px_40px_rgba(0,0,0,0.1)]"
               />
               <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-8 bg-brand-orange/10 blur-3xl rounded-full" />
            </div>
          </motion.div>
        )}

        {/* Tagline */}
        <div className="mt-32 text-center">
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="text-slate-300 text-[10px] md:text-xs font-black uppercase tracking-[0.5em]"
            >
              PKROUTEX — Redefining The Future Of Indian Logistics — 2024
            </motion.p>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedTruck && (
          <FleetModal 
            truck={selectedTruck} 
            onClose={() => setSelectedTruck(null)} 
          />
        )}
      </AnimatePresence>

      {/* GSAP Hover Glow Mouse Follower */}
      <div className="fleet-glow pointer-events-none fixed top-0 left-0 w-[400px] h-[400px] bg-brand-orange/[0.05] blur-[100px] rounded-full z-0 opacity-0 md:block hidden" />
    </section>
  );
};

export default Fleet;
